import React from "react";
import  Table from '../base-table';
import type { TableRef } from '../base-table';
import type { ColumnDef, SortingState, ColumnFiltersState, ColumnOrderState, RowSelectionState, PaginationState, ExpandedState, TableMeta } from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { EquipmentVehicle } from "../../graphql/graphql";
import { TableMessage } from "../../shared/TableMessage";
import { TableSpinner } from "../../shared/TableSpinner";

interface EquipmentVehiclesTableProps {
  tableRef: React.Ref<TableRef>;
  columns?: () => ColumnDef<EquipmentVehicle, any>[];
  data: EquipmentVehicle[];
  tableTitle?: React.ReactNode;
  getRowId?: (originalRow: EquipmentVehicle) => string;
  enableRowSelection?: boolean;
  enableColumnFiltersSidePanel?: boolean;
  enablePagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageIndex?: number;
  pageSize?: number;
  totalCount?: number;
  state: {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    setSorting: (newSorting: SortingState) => void;
    setColumnFilters: (newFilters: ColumnFiltersState) => void;
    columnOrder: ColumnOrderState;
    setColumnOrder: (newOrder: ColumnOrderState) => void;
    setRowSelection: (newRowSelection: RowSelectionState) => void;
    rowSelection?: RowSelectionState;
    resetRowSelection?: (defaultState?: boolean) => void;
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (newVisibility: Record<string, boolean>) => void;
    pagination?: { totalCount?: number }
    globalSearch?: string;
    expanded?: ExpandedState;
    columnFiltersSidePanelIsOpen?: boolean; 
  };
  isLoading: boolean;
  error: any;
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (filter: string) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
  meta: TableMeta<EquipmentVehicle>;
  renderExpandedContent?: (vehicle: EquipmentVehicle) => React.ReactNode;
}

export const EquipmentVehiclesTable = React.memo(
    function EquipmentVehiclesTable({ 
        tableRef,
        columns,
        data,
        tableTitle,
        state,
        isLoading,
        error,
        getRowId,
        pageIndex = 0,
        pageSize = 10,
        totalCount,
        enablePagination,
        onPaginationChange,
        enableGlobalFilter,
        onGlobalFilterChange,
        onExpandedChange,
        meta,
        renderExpandedContent,
        enableColumnFiltersSidePanel,
        onColumnFiltersSidePanelOpenChange,
    }: EquipmentVehiclesTableProps & {
        onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
    }) {


    // if (isLoading) return <Skeleton height={60} width={"100%"} />;
    if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

    if (data) {
        return (
            <div style={{ height: "100%" }}>
        <Table
            className="equipment-vehicles-table"
            fullWidth
            ref={tableRef}
            columns={columns ? (columns() as any) : []}
            getCoreRowModel={getCoreRowModel()}
            data={data}
            tableTitle={tableTitle}
            noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Vehicles Found" />}
            enableColumnFilters={false}
            enableColumnFiltersSidePanel={enableColumnFiltersSidePanel}
            enableSorting={true}
            disableStickyHeader={false}
            allowMultiSort={true}
            enableRowSelection={true}
            enablePagination={enablePagination}
            manualPagination={true}
            manualFiltering={true}
            manualSorting={true}
            enableMultiRowSelection={false}
            enableGlobalFilter={enableGlobalFilter}
            initialGlobalFilter={state.globalSearch}
            onPaginationChange={onPaginationChange}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={state.pagination?.totalCount}
            state={{
                columnFilters: state.columnFilters,
                sorting: state.sorting,
                columnOrder: state.columnOrder,
                setColumnOrder: state.setColumnOrder,
                rowSelection: state.rowSelection,
                columnVisibility: state.columnVisibility,
                expanded: state.expanded,
                columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen, 
            }}
            emptyState={{
            title: "No Equipment Found",
            description: "There are no vehicles to display at this time.",
            }}
            onSortChange={state.setSorting}
            onColumnFiltersChange={state.setColumnFilters}
            onRowSelectionChange={state.setRowSelection}
            onGlobalFilterChange={onGlobalFilterChange}
            getRowId={getRowId}
            resetRowSelection={state.resetRowSelection}
            expanded={state.expanded}
            onExpandedChange={onExpandedChange}
            onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
            meta={meta}
            renderExpandedContent={renderExpandedContent}
            noDataText={isLoading ? "Loading transport vehicles..." : "No vehicles found."}
            globalFilterPlaceholder="Search Vehicles..."
            globalFilterLiveSearch={true}
        />
        </div>
        );
    }
    return <></>;
});

